import { TextField, Tooltip, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import React from 'react'
import { useHistory } from 'react-router-dom'

import {
  createAtHomeListing,
  deleteAtHomeListing,
  fetchAtHomeApartments,
  fetchAtHomeCategories,
  fetchAtHomeListings,
  fetchAtHomeTeachers,
  updateAtHomeListing
} from 'api/atHome'
import Button from 'app/components/generic/Button'
import { navigationPaths } from 'app/config'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

import BookModuleDialog from './BookModuleDialog'
import { GRADES, WEEKDAYS, listingRow, fmtDate, isoLocal } from './format'
import AtHomeCrudPage from './shared/AtHomeCrudPage'

// This page IS the Regular tab: Trial is Slots.js on the same TabbedPage
// (index.js), so every row here is a regular session and the column is a
// constant rather than a guess off the row.
const SESSION_TYPE = 'Regular'

// Session Style comes from the category's STYLE, never from its name — new
// categories appear all the time (owner, 2026-10-19). 'module' is sold as
// Projects, 'regular' as Monthly; that wording is what is live in the app.
const STYLE_LABELS = { module: 'Projects', regular: 'Monthly' }
const styleLabel = (style) => STYLE_LABELS[style] || ''

// The shared fmtDate leaves the year off, which a listing's window cannot
// afford: it is a year long (1 Oct 2026 - 1 Oct 2027 would read as two 1 Octs).
const fmtDay = (iso) => {
  if (!iso) return ''
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

// The same calendar day a year on, as YYYY-MM-DD. 29 Feb lands on 1 Mar,
// which the Date constructor does for us.
const plusOneYear = (iso) => {
  if (!iso) return ''
  const [y, m, d] = iso.split('-').map(Number)
  return isoLocal(new Date(y + 1, m - 1, d))
}

// The window as the API sends it: snake_case, like start_time. The camelCase
// spelling is accepted too, so the new column cannot come back silently blank
// if the serializer lands with the other naming.
const windowOf = (l) => ({
  start_date: l.start_date || l.startDate || '',
  end_date: l.end_date || l.endDate || ''
})

const windowLabel = ({ start_date: from, end_date: to }) => {
  if (from && to) return `${fmtDay(from)} - ${fmtDay(to)}`
  if (from) return `From ${fmtDay(from)}`
  if (to) return `Until ${fmtDay(to)}`
  return '-'
}

const START_LABEL = 'First date this listing runs from'

// --- A window running out ---------------------------------------------------
// A renewal is validated exactly like a first sale (services/plan.py:
// renew_plan -> create_plan -> validate, and window_error refuses any session
// date past the listing's end_date), so once a monthly listing's end date is
// behind us the families on it cannot renew at all. The owner's call
// (2026-10-19) is to warn ops here, in time to extend the window, rather than
// let a renewal skip the check.
//
// 60 days, because that is about the longest commitment a listing can still
// carry entirely inside its own window:
//   · monthly — a plan runs start .. start + 1 month − 1 day (month_end) and a
//     renewal starts the day after this one ends, so a renewal booked today
//     already needs ~31 days of window. 60 leaves ops one whole extra cycle.
//   · projects — every module on dev is 8 sessions (13 of 13), which once a
//     week is 8 weeks = 56 days.
// The data cannot pick the number for us: migration 0028 back-filled all 205
// dev listings to 2027-10-01, so every threshold from 0 to 120 days counts the
// same 0 rows today.
const EXPIRY_WARN_DAYS = 60

// The three words the Dates filter offers. The cell says more than this (how
// many days, the way Availability says "Booked until …"); the filter stays
// coarse so "show me the expiring ones" is one pick.
const EXPIRED = 'Expired'
const ENDING = 'Ending soon'
const OPEN = 'Open'

// Whole days from today to `iso`, negative once it is past. Local calendar days
// on both sides — never Date.parse of a YYYY-MM-DD, which is UTC and so still
// yesterday in India until 5:30 am.
const daysUntil = (iso) => {
  if (!iso) return null
  const [y, m, d] = iso.split('-').map(Number)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((new Date(y, m - 1, d) - today) / 86400000)
}

// Already broken reads differently from a deadline (owner's ask): "Expired 5
// days ago" against "Ends in 12 days". A NULL end date is unbounded everywhere
// the API reads it (models/listing.py), so it is Open — not a thing to chase.
const expiryOf = (endIso) => {
  const days = daysUntil(endIso)
  if (days === null) return { token: OPEN, warning: '' }
  if (days < 0)
    return {
      token: EXPIRED,
      warning:
        days === -1 ? `${EXPIRED} yesterday` : `${EXPIRED} ${-days} days ago`
    }
  if (days <= EXPIRY_WARN_DAYS)
    return {
      token: ENDING,
      warning:
        days === 0
          ? 'Ends today'
          : days === 1
          ? 'Ends tomorrow'
          : `Ends in ${days} days`
    }
  return { token: OPEN, warning: '' }
}

// The warning rides inside the Dates cell behind this marker: the cell stays
// one honest string (which is what the header dropdown, the sort and the CSV
// download read) and the renderer can still colour the tail on its own. The two
// dates are joined by ' - ', so an em dash cannot collide with them.
const WARN_MARK = ' — '

const EXPIRY_HINTS = {
  [EXPIRED]:
    'This window has already ended, so the families on this listing cannot renew and nothing new can be booked on it. Extend the end date.',
  [ENDING]:
    'Every session of a booking has to fall inside this window, so renewals stop going through once the end date passes. Extend it before then.'
}

const useStyles = makeStyles((theme) => ({
  expired: { color: theme.palette.error.main, fontWeight: 600 },
  ending: { color: theme.palette.warning?.dark || '#b26a00', fontWeight: 600 }
}))

const COLUMNS = [
  // Same order as Schedules → Trial (owner, 2026-10-21).
  { name: 'sessionTypeLabel', label: 'Session Type' },
  { name: 'categoryName', label: 'Category' },
  { name: 'sessionStyleLabel', label: 'Session Style' },
  { name: 'teacherName', label: 'Teacher' },
  { name: 'weekdayName', label: 'Day', options: { keepOrder: true } },
  { name: 'timeLabel', label: 'Time', options: { keepOrder: true } },
  // No keepOrder: this dropdown lists the three window words (below), not the
  // windows themselves, so A→Z is a stable order for them.
  { name: 'datesLabel', label: 'Dates' },
  { name: 'apartmentName', label: 'Apartment' },
  { name: 'gradesLabel', label: 'Grades' },
  { name: 'availabilityLabel', label: 'Availability' },
  { name: 'bookedBy', label: 'Booked by' },
  { name: 'notes', label: 'Notes', options: { wrap: true } }
]

/**
 * Regular work is sold as modules on recurring weekly listings (owner's doc,
 * 2026-10-06): teacher · category · weekday · time · apartment · grades. A
 * parent books a module on one listing per session a week (1 to 7, same teacher);
 * the 8 dated sessions (8 weeks once a week, 4 weeks twice a week, …) are generated from them. This page lists
 * and edits the listings; "Book module" starts a plan on one of them.
 */
// mode 'schedule' = list, add, edit, delete the weekly listings.
// mode 'book' = book a module on them (management, 2026-10-09: different teams).
const AtHomeListings = ({ mode = 'schedule' }) => {
  const [categories, setCategories] = React.useState([])
  const [apartments, setApartments] = React.useState([])
  const [teachers, setTeachers] = React.useState([])

  /**
   * Keep a teacher in the option list even when the latest search did not
   * return them -- the same guard `Slots.js` carries, for the same two cases.
   *
   * EDITING a listing: the row's own teacher may not be in the first 200, and
   * MUI renders an unmatched value as an empty box. The value still saves, but
   * a blank teacher on an edit form is how someone picks the wrong person.
   * AFTER a search: once a teacher is chosen, typing again replaces the list,
   * and the chosen row must not vanish from under the selection.
   */
  const rememberTeacher = React.useCallback((t) => {
    if (!t || !t.uuid) return
    setTeachers((prev) =>
      prev.some((x) => x.uuid === t.uuid) ? prev : [t, ...prev]
    )
  }, [])
  const history = useHistory()
  const [booking, setBooking] = React.useState(null)
  const [refreshKey, setRefreshKey] = React.useState(0)
  // How many of the rows on screen have a window problem, for the count above
  // the grid. Filled from fetchList, which is the only place that has the rows.
  const classes = useStyles()

  React.useEffect(() => {
    fetchAtHomeCategories()
      .then(setCategories)
      .catch(() => {})
    fetchAtHomeApartments()
      .then(setApartments)
      .catch(() => {})
    fetchAtHomeTeachers()
      .then(setTeachers)
      .catch(() => {})
  }, [])

  // MULTI-SELECT since 2026-10-21 (owner). `AtHomeListingSerializer.grades`
  // is a ListField and the table has always printed the whole set
  // ("UKG, 1, 2, ... 8"), so a one-class picker was the only place that
  // pretended a listing runs for a single class.
  //
  // This replaces a scalar field that wrapped its value in an array, plus a
  // `_gradesStored` heuristic: a listing storing 1-8 showed "1", and saving
  // kept all eight ONLY because the pick still equalled the first stored
  // value. Pick anything else and the other seven vanished silently. With a
  // real multi-select the ticks ARE the answer, so the guess goes.
  const gradesPayload = (v) =>
    Array.isArray(v.grades) ? v.grades : [v.grades].filter(Boolean)

  // The form collects several days; each becomes its own listing.
  const createListings = async ({ weekdays, ...payload }) => {
    // `weekdays` is a SINGLE select since 2026-10-19, but this still normalises
    // through an array: the loop below is what the multi-day form used, and a
    // scalar would blow up on .map. Keeping the array shape means the day field
    // can go back to multi-select without touching this.
    const days = []
      .concat(weekdays || [])
      .filter((d) => d !== '')
      .map(Number)
    if (!days.length) {
      eventAlert('Pick at least one day of the week', constants.ERROR)
      throw new Error('no weekday')
    }
    for (const weekday of days) {
      // sequential, so a clash error names the day that failed
      // eslint-disable-next-line no-await-in-loop
      await createAtHomeListing({ ...payload, weekday })
    }
  }

  const fetchList = React.useCallback(async () => {
    const all = (await fetchAtHomeListings({})).map((l) => {
      const dates = windowOf(l)
      const { token, warning } = expiryOf(dates.end_date)
      return {
        ...listingRow(l),
        // Normalised here so the form (rowToForm spreads the row) and the
        // Dates cell both read one spelling.
        ...dates,
        sessionTypeLabel: SESSION_TYPE,
        sessionStyleLabel: styleLabel(l.categoryStyle),
        datesLabel: warning
          ? `${windowLabel(dates)}${WARN_MARK}${warning}`
          : windowLabel(dates),
        // The dropdown filters on the window word, not on the dates in the
        // cell — the same split Availability makes between "Booked until Fri,
        // 9 Oct" in the cell and "Booked" in its filter.
        datesLabelFilters: [token]
      }
    })
    const rows = mode === 'book' ? all.filter((r) => r.active) : all
    return rows
  }, [mode])

  // The Dates cell: the window, then its warning in the colour that says which
  // kind of warning it is. Everything else keeps the grid's plain text cells.
  const columns = React.useMemo(
    () =>
      COLUMNS.map((col) =>
        col.name !== 'datesLabel'
          ? col
          : {
              ...col,
              options: {
                ...(col.options || {}),
                customBodyRender: (value) => {
                  const [dates, warning] = String(value || '').split(WARN_MARK)
                  if (!warning) return dates
                  const gone = warning.startsWith(EXPIRED)
                  return (
                    <Tooltip title={EXPIRY_HINTS[gone ? EXPIRED : ENDING]}>
                      <span>
                        {dates}
                        <span
                          className={gone ? classes.expired : classes.ending}
                        >
                          {`${WARN_MARK}${warning}`}
                        </span>
                      </span>
                    </Tooltip>
                  )
                }
              }
            }
      ),
    [classes]
  )

  // Module and regular (monthly) categories both list the same way (owner,
  // 2026-10-10): a teacher, a day, a time, an apartment, grades.
  const moduleCategories = categories.filter((c) => c.status !== 'inactive')

  // Category first (owner, 2026-10-07): the rest of the form depends on how
  // the category is sold. Both styles are offered here — `moduleCategories`
  // above only drops inactive ones — and the `_style` note below tells the
  // user which of the two they picked.
  const chosen = (values) => values.category
  const styleOf = (values) =>
    categories.find((c) => c.uuid === values.category)?.style
  const fields = [
    {
      name: 'category',
      label: 'Category (the subject it shows under in the app)',
      type: 'select',
      required: true,
      options: moduleCategories.map((c) => ({
        value: c.uuid,
        label: c.name
      }))
    },
    {
      name: '_style',
      type: 'note',
      showIf: chosen,
      label: (v) =>
        styleOf(v) === 'regular'
          ? 'Sold as monthly classes: one to seven days a week with the same teacher (one listing per day), one month from the start date, renewed month by month.'
          : 'Sold as a project: a fixed set of sessions, one to seven days a week with the same teacher (one listing per day). Parents book it on the listing below.'
    },
    {
      name: 'teacher',
      label: 'Teacher who visits (the parent sees the name)',
      type: 'searchselect',
      required: true,
      showIf: chosen,
      /**
       * SEARCHED ON THE SERVER, as the New Session form already was.
       *
       * `admin/teachers/` returns the first 200 by first name and filters on
       * name OR phone when given `q`. This form fetched it once with no query
       * and let MUI filter those 200 in the browser, so every teacher from
       * about "Bi" onwards did not exist as far as this dialog was concerned
       * -- 1,250 on prod, and no request fired to say otherwise.
       *
       * The same fix landed on `Slots.js` on 2026-10-21 and missed this form,
       * which has its own copy of the picker.
       */
      onSearch: (text) =>
        fetchAtHomeTeachers(text)
          .then(setTeachers)
          .catch(() => {}),
      options: teachers.map((t) => ({
        value: t.uuid,
        label: `${t.name} · ${t.phone}`
      }))
    },
    {
      name: 'apartment',
      label: 'Apartment (only families living here are offered this time)',
      type: 'searchselect',
      required: true,
      showIf: chosen,
      options: apartments
        .filter((a) => a.active)
        .map((a) => ({
          value: a.uuid,
          label: a.area ? `${a.name} (${a.area})` : a.name
        }))
    },
    {
      // New listing: several days at once → one listing per day (owner,
      // 2026-10-08: a teacher who comes Tuesday and Friday is set up in one go).
      name: 'weekdays',
      label: 'Day of the week',
      type: 'select',
      required: true,
      showIf: (v) => chosen(v) && !v.uuid,
      options: WEEKDAYS.map((d, i) => ({ value: String(i), label: d }))
    },
    {
      name: 'weekday',
      label: 'Day of the week the class runs',
      type: 'select',
      required: true,
      showIf: (v) => chosen(v) && Boolean(v.uuid),
      options: WEEKDAYS.map((d, i) => ({ value: String(i), label: d }))
    },
    {
      name: 'start_time',
      label: 'Start time (the parent sees this time)',
      type: 'time',
      required: true,
      showIf: chosen
    },
    {
      name: 'duration_minutes',
      label: 'How long the class runs, in minutes (sets the finish time)',
      type: 'number',
      showIf: chosen
    },
    {
      name: '_window',
      type: 'note',
      showIf: chosen,
      label: (v) =>
        styleOf(v) === 'regular'
          ? 'Monthly classes: enter the first and the last date this listing is on offer.'
          : 'Projects: enter the first date; the last is filled in a year later and can be changed.'
    },
    {
      // 'date' in AtHomeFormModal is a plain controlled TextField with no
      // onSelect/clears hook (only 'select' and 'autocomplete' have one), so
      // the end-date prefill goes through the modal's existing escape hatch:
      // type 'custom' -> render({ values, setField }). No new field type, and
      // `required` still works — the modal's check reads values[name] and does
      // not care about the type.
      name: 'start_date',
      label: START_LABEL,
      type: 'custom',
      required: true,
      showIf: chosen,
      render: ({ values, setField }) => (
        <TextField
          type="date"
          label={START_LABEL}
          variant="outlined"
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={values.start_date || ''}
          onChange={(e) => {
            const next = e.target.value
            setField('start_date', next)
            // Projects only: the end follows the start by a year until ops
            // type an end of their own, which is then left alone.
            if (styleOf(values) !== 'module') return
            const wasAuto = plusOneYear(values.start_date)
            if (!values.end_date || values.end_date === wasAuto)
              setField('end_date', plusOneYear(next))
          }}
        />
      )
    },
    {
      name: 'end_date',
      label: 'Last date this listing runs until',
      type: 'date',
      required: true,
      showIf: chosen
    },
    {
      name: 'grades',
      label: 'Classes this listing is for',
      type: 'multiselect',
      required: true,
      showIf: chosen,
      options: GRADES
    },
    {
      name: 'active',
      label: 'Show in the parent app (untick and it disappears completely)',
      type: 'checkbox',
      showIf: chosen
    },
    {
      name: 'notes',
      label: 'Internal note — parents and teachers never see this',
      type: 'textarea',
      showIf: chosen
    }
  ]

  return (
    <>
      <AtHomeCrudPage
        subtitle={
          mode === 'book'
            ? "Each row is a teacher's weekly class time with space to book a project or monthly classes."
            : "Each row is a teacher's weekly class time. Families book projects or monthly classes on it."
        }
        entityLabel="Listing"
        fetchList={fetchList}
        createItem={mode === 'schedule' ? createListings : undefined}
        updateItem={mode === 'schedule' ? updateAtHomeListing : undefined}
        deleteItem={mode === 'schedule' ? deleteAtHomeListing : undefined}
        columns={columns}
        // A listing held by a module keeps its promise (day, time, teacher,
        // apartment); only grades and notes may change until the module ends.
        fields={(row) =>
          row && row.busyUntil
            ? [
                {
                  name: '_held',
                  type: 'note',
                  label: `Held by ${row.bookedBy || 'a module'} until ${fmtDate(
                    row.busyUntil
                  )}: day, time, teacher and apartment are locked until then.`
                },
                ...fields.filter((f) => ['grades', 'notes'].includes(f.name))
              ]
            : fields
        }
        emptyValues={{
          duration_minutes: 60,
          grades: [],
          active: true,
          weekdays: '',
          weekday: '',
          start_time: '',
          start_date: '',
          end_date: ''
        }}
        rowToForm={(row) => {
          // The row's teacher may not be in the first 200; without this the
          // edit dialog opens with the teacher box blank.
          rememberTeacher({ uuid: row.teacher, name: row.teacherName })
          return {
            ...row,
            weekday: String(row.weekday),
            // The stored set, straight in: the picker is a multi-select, so the
            // ticks a listing opens with ARE what it stores. A live listing on
            // the full 1-8 ladder therefore shows eight ticks and saves eight,
            // which is what the single-select and its `_gradesStored` guess
            // existed to fake.
            grades: row.grades || [],
            start_time: (row.start_time || '').slice(0, 5)
          }
        }}
        formToPayload={(v) =>
          v.uuid && v.busyUntil
            ? { grades: gradesPayload(v), notes: v.notes || '' }
            : {
                category: v.category,
                teacher: v.teacher,
                apartment: v.apartment,
                weekday: Number(v.weekday),
                weekdays: v.weekdays,
                start_time: v.start_time,
                // Dates are nullable columns: '' is not a date the API can
                // parse, so an unset window goes as null.
                start_date: v.start_date || null,
                end_date: v.end_date || null,
                duration_minutes: Number(v.duration_minutes) || 60,
                grades: gradesPayload(v),
                active: v.active !== false,
                notes: v.notes || ''
              }
        }
        refreshKey={`${mode}-${refreshKey}`}
        canDelete={(row) => !row.busyUntil}
        deleteMessage={(row) =>
          `Delete the ${row.categoryName} listing with ${row.teacherName} on ${row.weekdayName}s at ${row.timeLabel}? Parents will no longer be able to book a module on it.`
        }
        rowActions={(row) =>
          row.active ? (
            <>
              {row.busyUntil && mode === 'book' && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    window.localStorage.setItem(
                      'atHome.bookings.tab',
                      'modules'
                    )
                    history.push(navigationPaths.atHomeBookings)
                  }}
                >
                  view module
                </Button>
              )}
              {mode === 'book' && (
                <Button size="small" onClick={() => setBooking(row)}>
                  {row.busyUntil ? 'book next' : 'book'}
                </Button>
              )}
            </>
          ) : null
        }
      />
      <BookModuleDialog
        listing={booking}
        open={Boolean(booking)}
        onClose={() => setBooking(null)}
        onBooked={() => setRefreshKey((k) => k + 1)}
      />
    </>
  )
}

AtHomeListings.propTypes = {}

export default AtHomeListings
